declare global {
  interface Window {
    __FW_DISABLE_BACKEND_AB_TESTING__?: boolean
  }
}

/**
 * Attaches backend A/B testing functionality to the global _fwn object.
 * Experiments are loaded on demand via initializeBackendABTesting()
 * (e.g. when a web component renders), not on bundle attach.
 */
export function attach() {
  const propName = 'backendABTesting'

  // Check if backend A/B testing is disabled (e.g., in tests)
  if (
    typeof window !== 'undefined' &&
    window.__FW_DISABLE_BACKEND_AB_TESTING__
  ) {
    // Attach no-op methods when disabled
    Object.defineProperty(window._fwn, propName, {
      get: () => ({
        initializeBackendABTesting: () => Promise.resolve(),
        getBackendExperiments: () => [],
        getExperimentById: () => undefined,
        getExperimentByType: () => undefined,
        isInExperimentVariant: () => false,
        getBackendExperimentsForTracking: () => [],
        refreshExperiments: () => Promise.resolve(),
        resetBackendABTesting: () => {},
      }),
    })
    return
  }

  let backendABTestingModule: any = null

  // Load the backend AB testing module
  async function getBackendABTestingModule() {
    if (!backendABTestingModule) {
      backendABTestingModule =
        await import('@core/abTesting/backend/backendABTesting')
    }
    return backendABTestingModule
  }

  if (
    typeof window !== 'undefined' &&
    !Object.prototype.hasOwnProperty.call(window._fwn, propName)
  ) {
    // Attach methods to global _fwn object
    Object.defineProperty(window._fwn, propName, {
      get: () => ({
        initializeBackendABTesting: async () => {
          const module = await getBackendABTestingModule()
          return module.initializeBackendABTesting()
        },
        getBackendExperiments: () => {
          if (!backendABTestingModule) {
            return []
          }
          return backendABTestingModule.getBackendExperiments()
        },
        getExperimentById: (experimentId: string) => {
          if (!backendABTestingModule) {
            return undefined
          }
          return backendABTestingModule.getExperimentById(experimentId)
        },
        getExperimentByType: (baseExperimentType: string) => {
          if (!backendABTestingModule) {
            return undefined
          }
          return backendABTestingModule.getExperimentByType(baseExperimentType)
        },
        isInExperimentVariant: (
          baseExperimentType: string,
          variantType: string,
        ) => {
          if (!backendABTestingModule) {
            return false
          }
          return backendABTestingModule.isInExperimentVariant(
            baseExperimentType,
            variantType,
          )
        },
        getBackendExperimentsForTracking: () => {
          if (!backendABTestingModule) {
            return []
          }
          return backendABTestingModule.getBackendExperimentsForTracking()
        },
        refreshExperiments: async () => {
          const module = await getBackendABTestingModule()
          return module.refreshExperiments()
        },
        resetBackendABTesting: async () => {
          const module = await getBackendABTestingModule()
          return module.resetBackendABTesting()
        },
      }),
    })
  }
}
